import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getEvents, 
  cancelEventRegistration, 
  getUserEventRegistrations,
  Event, 
  EventRegistration 
} from '../../services/eventService';
import { getSession, User } from '../../services/api';
import { EventForm } from '../../components/EventForm';
import { EventCard } from '../../components/EventCard';
import { LocationModal } from '../../components/LocationModal';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';  // Import the CSS for toasts

import styles from './EventOverviewPage.module.css';

interface EventOverviewProps {}

export const Event_Overview_Page: React.FC<EventOverviewProps> = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [registrations, setRegistrations] = useState<EventRegistration[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [showPastEvents, setShowPastEvents] = useState<boolean>(false);
  const navigate = useNavigate();

  // Fetch user session, events, and registrations
  useEffect(() => {
    const fetchUserAndEvents = async () => {
      setLoading(true);
      try {
        // Get user session
        const sessionResponse = await getSession();
        if (sessionResponse && sessionResponse.authenticated && sessionResponse.user) {
          setUser(sessionResponse.user);
          if (sessionResponse.roles && Array.isArray(sessionResponse.roles)) {
            setRoles(sessionResponse.roles);
          }
        }

        // Get events
        const eventsData = await getEvents();
        setEvents(eventsData);

        // Get user registrations
        const registrationsData = await getUserEventRegistrations();
        setRegistrations(registrationsData);

      } catch (err) {
        setError('Failed to load events. Please try again later.');
        console.error('Error loading events:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndEvents();
  }, []);

  // Initialize Bootstrap modal properly
  useEffect(() => {
    // Ensure modal is properly closed when hidden
    const eventModal = document.getElementById('eventModal');
    if (eventModal) {
      eventModal.addEventListener('hidden.bs.modal', () => {
        // Reset form when modal is closed
        // This is a backup in case the form isn't reset properly
        const form = document.getElementById('eventForm') as HTMLFormElement;
        if (form) {
          form.reset();
        }
      });
    }
  }, []);

  // Refresh events list and registrations
  const refreshEvents = useCallback(async () => {
    try {
      const eventsData = await getEvents();
      setEvents(eventsData);
      
      // Refresh registrations
      const registrationsData = await getUserEventRegistrations();
      setRegistrations(registrationsData);
    } catch (err) {
      console.error('Error refreshing events:', err);
    }
  }, []);

  // Filter events by future/past status
  const futureEvents = events.filter(event => event.is_future);
  const pastEvents = events.filter(event => !event.is_future);

  // Handle event registration cancellation - using useCallback to avoid recreating the function on each render
  const handleCancelEvent = useCallback(async (eventId: string) => {
    try {
      const success = await cancelEventRegistration(eventId);
      if (success) {
        // Refresh events list
        await refreshEvents();
        toast.success('Event participation cancelled successfully.', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      } else {
        toast.error('Failed to cancel event participation. Please try again.', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      }
    } catch (err) {
      console.error('Error cancelling event:', err);
      toast.error('An error occurred while cancelling event participation.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    }
  }, [refreshEvents]);

  // Handler for copying registration link directly without modal
  const handleCopyRegistrationLink = useCallback((link: string) => {
    navigator.clipboard.writeText(link)
      .then(() => {
        toast.success('Registration link copied to clipboard!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        
        // Refresh the page to show updated data
        refreshEvents();
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
        toast.error('Failed to copy link. Please try again.', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      });
  }, [refreshEvents]);

  // No need to set up event handlers for modals since we're using toasts directly

  // Check if user is admin
  const isAdmin = roles.some(role => ['Admin', 'Event Admin', 'Event Reader'].includes(role));
  const canCreateEvent = roles.some(role => ['Admin', 'Event Admin'].includes(role));

  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className={`container ${styles.loadingContainer}`}>
        <div className={`spinner-border text-primary ${styles.loadingSpinner}`} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <h3>Loading events...</h3>
      </div>
    );
  }

  if (error) {
    return <div className={`container mt-4 text-danger ${styles.errorContainer}`}>{error}</div>;
  }

  return (
    <div className="container mt-4">
      {/* Toast Container with custom settings */}
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        className={styles.toastContainer}
      />
      
      {/* Breadcrumb Navigation */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item active"><a>Events</a></li>
        </ol>
      </nav>

      <div className={`card p-4 role-container ${styles.roleContainer}`}>
        <div className="row">
          <div className="col-12 col-md-6">
            <h3 className="mb-0">Upcoming Events</h3>
          </div>
          
          {canCreateEvent && (
            <div className={`col-12 col-md-6 d-flex justify-content-md-end align-items-end mt-2 mt-md-0 ${styles.actionBar}`}>
              <button 
                type="button" 
                className="btn btn-primary" 
                data-bs-toggle="modal" 
                data-bs-target="#eventModal"
              >
                <i className="bi bi-plus-lg"></i> Create Event
              </button>
            </div>
          )}
        </div>

        {/* Event Cards */}
        <div className={`row mt-3 g-3 ${styles.equalHeight}`}>
          {futureEvents.length > 0 ? (
            futureEvents.map(event => (
              <EventCard 
                key={event.Events_ID}
                event={event}
                registrations={registrations}
                userId={user?.User_ID}
                isAdmin={isAdmin}
                onCancelEvent={handleCancelEvent}
              />
            ))
          ) : (
            <div className="col-12 mt-3">
              <p>No upcoming events found.</p>
            </div>
          )}
        </div>

        <div className="row">
          <div className="col-12">
            <button 
              className={`btn btn-primary mb-3 ${!showPastEvents ? '' : styles.disabledButton}`}
              onClick={() => setShowPastEvents(!showPastEvents)}
            >
              {showPastEvents ? 'Hide Past Events' : 'Show Past Events'}
            </button>
          </div>
        </div>

        {showPastEvents && (
          <div className={`row g-3 ${styles.equalHeight}`}>
            {pastEvents.length > 0 ? (
              pastEvents.map(event => (
                <EventCard 
                  key={event.Events_ID}
                  event={event}
                  registrations={registrations}
                  userId={user?.User_ID}
                  isAdmin={isAdmin}
                  onCancelEvent={handleCancelEvent}
                />
              ))
            ) : (
              <div className="col-12">
                <p>No past events found.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Event Creation Modal */}
      {canCreateEvent && (
        <div className="modal fade" id="eventModal" tabIndex={-1} aria-labelledby="eventModalLabel" aria-hidden="true">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="eventModalLabel">Create New Event</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div className="modal-body">
                <EventForm onEventCreated={refreshEvents} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Event Location Modal */}
      <LocationModal />
    </div>
  );
};




